import React from "react";
import { isTaskMantisDesktop } from "../desktopUpdate";
import SettingsToggleRow from "./shared/SettingsToggleRow";
import { useCaptureHotkeyState } from "./shared/useCaptureHotkeyState";

/**
 * Settings section for the quick-capture global hotkey. Off until the user
 * turns it on — flipping the switch is what asks macOS for the Accessibility
 * permission the hotkey needs.
 *
 * @example
 * <DesktopCaptureSettingsSection />
 * // Renders "Quick capture" in Electron; nothing in the browser
 */
const DesktopCaptureSettingsSection = () => {
  const { state, busy, setEnabled, openAccessibilitySettings } = useCaptureHotkeyState();

  if (!isTaskMantisDesktop()) return null;

  // Enabled but untrusted means the user dismissed or denied the macOS
  // prompt: the hotkey is claimed and still won't fire, which is worth saying
  // out loud rather than leaving as a silently dead shortcut.
  const needsPermission = state.enabled && !state.accessibilityTrusted;
  const hotkeyTaken = state.enabled && !state.hotkeyRegistered;

  return (
    <section className="settings-general-section">
      <h3 className="settings-general-section__title">Quick capture</h3>
      <div className="settings-general-block settings-general-block--nested">
        <SettingsToggleRow
          label={`Capture with ${state.hotkey}`}
          description={`Select text in any app, then press ${state.hotkey} to add it as a task. Needs macOS Accessibility permission, which we'll ask for when you turn this on.`}
          pressed={state.enabled}
          onChange={(next) => {
            if (busy) return;
            void setEnabled(next);
          }}
        />
        {needsPermission ? (
          <p className="settings-general-section__desc settings-general-section__desc--warning" role="status">
            macOS hasn't granted Accessibility permission yet, so the shortcut won't
            fire.{" "}
            <button
              type="button"
              className="settings-general-link-button"
              onClick={openAccessibilitySettings}
            >
              Open System Settings
            </button>
          </p>
        ) : null}
        {hotkeyTaken ? (
          <p className="settings-general-section__desc settings-general-section__desc--warning" role="status">
            {state.hotkey} is already used by another app, so quick capture can't
            claim it. Free it up, then toggle this off and on again.
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default DesktopCaptureSettingsSection;
